// src/Components/Sessions/NewPatientSession.jsx
import React, { useRef, useState, useEffect } from "react";
import { Row, Col, Container, Button, Nav } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  getAppointmentById,
  getServiceById,
  fetchSessionByAppointmentId,
} from "../../api";
import { FollowUpSession } from "./FollowUpSession";
import { UpdateSession } from "./EditSession";

export default function NewPatientSession({ onMobileNavigate }) {
  const followUpRef = useRef();
  const navigate = useNavigate();

  // Only expect an appointment-based route
  const { appointmentId } = useParams();

  const [activeSection, setActiveSection] = useState("apptTemplate");
  const [appointment, setAppointment] = useState();
  const [service, setService] = useState();
  const [existingSessionId, setExistingSessionId] = useState(null);
  const [existingSession, setExistingSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Left sidebar (mobile)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // Always resolve a safe patientId (unregistered => "1")
  const effectivePatientId = appointment?.patientId || "1";

  const formattedDateTime =
    appointment?.start && !isNaN(new Date(appointment.start))
      ? new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(appointment.start))
      : "";

  const NotesHeader = () => {
    return (
      <div className="appt-sticky-header">
        <Row className="align-items-center">
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <p className="text-primary fw-bold mb-0">
                {formattedDateTime} &nbsp;-&nbsp;{" "}
                {service?.servicesName || "Loading service name..."}
              </p>
            </div>
          </Col>
        </Row>

        {/* Show all three tabs on both mobile & desktop */}

{isMobile ? (
  // MOBILE: show Template + Notepad + Media
  <Nav
    variant="pills"
    activeKey={activeSection}
    onSelect={(k) => setActiveSection(k || "apptTemplate")}
    className="px-2 pb-2"
    style={{ gap: 8 }}
  >
    {["apptTemplate", "apptNotepad", "apptMedia"].map((tab) => (
      <Nav.Item key={tab}>
        <Nav.Link eventKey={tab} className="py-1 px-3" style={{ borderRadius: 9999 }}>
          {{ apptTemplate: "Template", apptNotepad: "Note Pad", apptMedia: "Media" }[tab]}
        </Nav.Link>
      </Nav.Item>
    ))}
  </Nav>
) : (
  // DESKTOP: show Template + Notepad (NO Media)
  <div className="d-flex align-items-center gap-3 mt-2">
    {["apptTemplate", "apptNotepad"].map((tab) => (
      <Button
        key={tab}
        variant=""
        size="sm"
        className={`fs-6 px-0 bg-transparent border-0 rounded-0 ${
          activeSection === tab ? "border-bottom border-primary border-2 text-primary" : "text-primary"
        }`}
        style={{ fontWeight: activeSection === tab ? "bold" : "normal", lineHeight: "1.2" }}
        onClick={() => setActiveSection(tab)}
      >
        {{ apptTemplate: "Template", apptNotepad: "Note Pad" }[tab]}
      </Button>
    ))}
  </div>
)}

      </div>
    );
  };

  const renderAppointmentNotes = () => {
    if (!appointment) return <p>Loading appointment details...</p>;

    // If a session already exists for this appointment -> edit; else create new
    if (existingSessionId && existingSession) {
      return (
        <>
          <NotesHeader />
          <UpdateSession
            key={`edit-${existingSessionId}`}
            sessionId={existingSession._id}
            appointmentId={existingSession.sessionsAppointmentId}
            practiceId={existingSession.sessionsPracticeId}
            patientId={effectivePatientId}
            sections={existingSession.sections}
            notepadContentR={existingSession.notepadContent || ""}
            notepadContentType={existingSession.notepadContentType || ""}
            mediaFilesR={existingSession.mediaFiles || []}
            appointmentStatus={appointment.status}
            serviceName={service?.servicesName || ""}
            appointmentStart={appointment.start}
            templateId={existingSession.sessionsTemplateId}
            resourceId={appointment.resourceId}
            activeSection={activeSection}
            // After any save, go to /bookings (do not change appointment status here)
            onSaved={() => navigate("/bookings")}
          />
        </>
      );
    }

    return (
      <>
        <NotesHeader />
        <FollowUpSession
          key={`create-${appointmentId}`}
          ref={followUpRef}
          appointmentId={appointmentId}
          patientId={effectivePatientId}
          serviceName={service?.servicesName || ""}
          resourceId={appointment.resourceId}
          appointmentStart={appointment.start}
          appointmentStatus={appointment.status}
          practiceId={appointment.practiceId}
          activeSection={activeSection}
          // After submit/save, go to /bookings (do not change appointment status here)
          onUpdate={() => navigate("/bookings")}
          onSaved={() => navigate("/bookings")}
        />
      </>
    );
  };

  const doAndCollapse = (fn, payload) => {
    try {
      fn?.();
    } finally {
      setIsSidebarCollapsed(true);
      onMobileNavigate?.(payload);
    }
  };

  // --- Effects ---
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load appointment + service
  useEffect(() => {
    async function loadAppointment() {
      try {
        if (!appointmentId) return;

        const appt = await getAppointmentById(appointmentId);
        setAppointment(appt);

        if (appt?.serviceId) {
          const svc = await getServiceById(appt.serviceId);
          setService(svc);
        }
      } catch (error) {
        console.error("Error fetching appointment/service:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  // Load existing session (by appointment)
  useEffect(() => {
    async function loadSession() {
      try {
        if (!appointmentId) return;
        const s = await fetchSessionByAppointmentId(appointmentId);
        if (s && s._id) {
          setExistingSessionId(s._id);
          setExistingSession(s);
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      }
    }
    loadSession();
  }, [appointmentId]);

  if (isLoading) return <p>Loading session data...</p>;

  // Sidebar with only "Appointment Notes"
  const sidebarItems = [{ key: "appointment", label: "Appointment Notes" }];
  const appointmentKeys = isMobile
  ? ["apptTemplate", "apptNotepad", "apptMedia"]
  : ["apptTemplate", "apptNotepad"];

  const parentIsActive = appointmentKeys.includes(activeSection);

  const titleMap = {
    apptTemplate: "APPOINTMENT NOTES - TEMPLATE",
    apptNotepad: "APPOINTMENT NOTES - NOTE PAD",
    apptMedia: "APPOINTMENT NOTES - MEDIA",
  };

  return (
    <div className="ms-1">
      {/* Mobile-only Collapsible Arrow Button */}
      {isMobile && (
        <div
          className="position-fixed d-flex align-items-center justify-content-center bg-primary text-white shadow-lg"
          style={{
            left: isSidebarCollapsed ? "10px" : "250px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "20px",
            height: "40px",
            borderRadius: "0 8px 8px 0",
            cursor: "pointer",
            transition: "left 0.3s ease",
            zIndex: 1050,
          }}
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        >
          <i
            className={`bi ${
              isSidebarCollapsed ? "bi-chevron-right" : "bi-chevron-left"
            }`}
            style={{ fontSize: "18px" }}
          />
        </div>
      )}

      {/* Overlay for mobile when sidebar is open */}
      {!isSidebarCollapsed && isMobile && (
        <div
          className="position-fixed w-100 h-100 bg-dark bg-opacity-50"
          style={{ top: 0, left: 0, zIndex: 1040 }}
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      <Container fluid>
        <Row className="g-0">
          {/* LEFT SIDEBAR */}
          {isMobile ? (
            <div
              className="position-fixed bg-white shadow-lg d-flex flex-column"
              style={{
                left: isSidebarCollapsed ? "-250px" : "0",
                top: "0",
                width: "250px",
                height: "100vh",
                transition: "left 0.3s ease",
                zIndex: 1045,
              }}
            >
              <div className="p-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold text-primary">Menu</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setIsSidebarCollapsed(true)}
                  >
                    ×
                  </Button>
                </div>
              </div>

              <div className="flex-grow-1 overflow-auto">
                <div className="p-3 pt-3">
                  <div className="text-muted small mb-2">Session</div>
                  {sidebarItems.map((item) => {
                    const isActive =
                      item.key === "appointment"
                        ? parentIsActive
                        : activeSection === item.key;

                    return (
                      <div key={item.key} className="mb-2">
                        <Button
                          variant={isActive ? "primary" : "outline-primary"}
                          className="w-100 text-start"
                          onClick={() => {
                            setActiveSection("apptTemplate"); // default sub-tab
                            doAndCollapse(null, "session");
                          }}
                        >
                          {item.label}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <Col md={2} className="pt-1 g-0">
              <Container className="bg-white vh-100 shadow-sm g-0 p-3 rounded-0">
                <div className="text-center mb-3">
                  {sidebarItems.map((item) => {
                    const isActive =
                      item.key === "appointment"
                        ? parentIsActive
                        : activeSection === item.key;
                    return (
                      <div key={item.key}>
                        <Button
                          variant={isActive ? "primary" : "outline-primary"}
                          className="w-100 text-start mb-1 rounded-0"
                          onClick={() => {
                            setActiveSection("apptTemplate");
                          }}
                        >
                          {item.label}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Container>
            </Col>
          )}

          {/* MAIN CONTENT */}
          <Col md={isMobile ? 12 : 10}>
            <Container className="pt-1 bg-transparent pb-2 mb-0 ms-2 rounded">
  <h6 className="text-primary mt-1 fw-normal">

                {titleMap[activeSection] || "APPOINTMENT"}
              </h6>
              {renderAppointmentNotes()}
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
